import { useMemo } from "react";

import { filterOpenModelsForUser } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type { ExplicitLLModel } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { LLModel } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import type { ICountryObject } from "app/services/CountryService";

import type { IConfigurationOptions } from "./useConfigurationOptions";
import { useConfigurationOptions } from "./useConfigurationOptions";

const isCountrySupportedForChatbot = (
    chatbotName: LLModel,
    countryCode: string,
    configOptions: IConfigurationOptions | undefined,
): boolean => {
    if (!configOptions?.supportedCountriesByChatbot || !countryCode) {
        return false;
    }

    const supportedCountries = configOptions.supportedCountriesByChatbot[chatbotName];
    if (!supportedCountries) {
        return false;
    }

    const countryCodeUpper = countryCode.toUpperCase();
    return supportedCountries.some((country) => country.toUpperCase() === countryCodeUpper);
};

interface IUseFilteredChatbotsResult {
    filteredChatbots: ExplicitLLModel[];
    isLoading: boolean;
}

export const useFilteredChatbots = (selectedCountry: ICountryObject | null | undefined): IUseFilteredChatbotsResult => {
    const { data: configOptions, isLoading } = useConfigurationOptions();

    const filteredChatbots = useMemo<ExplicitLLModel[]>(() => {
        if (!selectedCountry?.code || !configOptions?.supportedCountriesByChatbot) {
            return [];
        }

        const enabledChatbots = Object.keys(configOptions.supportedCountriesByChatbot) as ExplicitLLModel[];
        const supportedLLMsForCountry = enabledChatbots.filter((chatbot) => {
            return isCountrySupportedForChatbot(chatbot, selectedCountry.code, configOptions);
        });

        return filterOpenModelsForUser(supportedLLMsForCountry).filter((model) => model !== LLModel.All);
    }, [selectedCountry?.code, configOptions]);

    return { filteredChatbots, isLoading };
};
